import { useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

// Plan from our database (linked to Razorpay)
export interface RazorpayPlan {
  id: string;
  razorpay_plan_id: string;
  name: string;
  description: string | null;
  type: 'token' | 'provider' | 'bundle';
  provider_count: number;
  token_count: number;
  amount: number;
  currency: string;
  period: string;
  interval: number;
  is_active: boolean;
}

// Subscription Types
export interface Subscription {
  id: string;
  business_id: string;
  plan_type: 'monthly' | 'annual';
  provider_count: number;
  token_count: number;
  amount: number;
  status: 'active' | 'pending' | 'expired' | 'cancelled' | 'cancelling';
  start_date: string;
  end_date: string;
  razorpay_subscription_id: string | null;
  razorpay_plan_id: string | null;
  quantity: number;
  payment_retry_count: number;
  last_payment_failure_at: string | null;
  grace_period_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSubscriptionRequest {
  plan_id: string;
  quantity: number;
}

export interface CreateSubscriptionResponse {
  success: boolean;
  message: string;
  data: {
    subscription: Subscription;
    payment: {
      subscription_id: string;
      key_id: string;
    } | null;
  };
}

export interface UpgradeSubscriptionRequest {
  plan_id: string;
  quantity: number;
}

export interface UpgradeSubscriptionResponse {
  success: boolean;
  message: string;
  data: {
    subscription: Subscription;
    proration: any;
    payment: {
      subscription_id: string;
      key_id: string;
    } | null;
  };
}

export interface VerifyPaymentRequest {
  subscription_id: string;
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface ValidateSubscriptionRequest {
  provider_count: number;
  token_count: number;
}

export interface ValidationError {
  type: 'provider' | 'token';
  message: string;
  current_value: number;
  selected_value: number;
  queue_name?: string;
}

export interface ValidateSubscriptionResponse {
  success: boolean;
  message: string;
  data: {
    is_valid: boolean;
    errors: ValidationError[];
    current_provider_count: number;
    peak_daily_tokens: number;
    per_day_usage: Record<number, number>;
  };
}

export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
  data: {
    subscription: Subscription;
  };
}

interface GetSubscriptionResponse {
  success: boolean;
  message: string;
  data: Subscription | null;
}

interface GetPlansResponse {
  success: boolean;
  message: string;
  data: {
    plans: RazorpayPlan[];
  };
}

export interface ActiveSubscriptionItem extends Subscription {
  plan_name: string | null;
  plan_type_category: string | null;
}

export interface ActiveSubscriptionsData {
  subscriptions: ActiveSubscriptionItem[];
  totals: {
    total_providers: number;
    total_tokens: number;
    total_monthly_cost: number;
  };
}

interface GetActiveSubscriptionsResponse {
  success: boolean;
  message: string;
  data: ActiveSubscriptionsData;
}

export interface TrialStatus {
  is_on_trial: boolean;
  trial_end_date: string | null;
  days_remaining: number;
  provider_count: number;
  token_count: number;
}

interface UseSubscriptionReturn {
  isLoading: boolean;
  error: string | null;
  subscription: Subscription | null;
  plans: RazorpayPlan[];
  activeSubscriptions: ActiveSubscriptionsData | null;
  trialStatus: TrialStatus | null;
  getSubscription: () => Promise<Subscription | null>;
  fetchActiveSubscriptions: () => Promise<ActiveSubscriptionsData | null>;
  fetchTrialStatus: () => Promise<TrialStatus | null>;
  fetchPlans: () => Promise<RazorpayPlan[]>;
  validateSubscription: (data: ValidateSubscriptionRequest) => Promise<ValidateSubscriptionResponse['data']>;
  createSubscription: (data: CreateSubscriptionRequest) => Promise<CreateSubscriptionResponse['data']>;
  upgradeSubscription: (data: UpgradeSubscriptionRequest) => Promise<UpgradeSubscriptionResponse['data']>;
  verifyPayment: (data: VerifyPaymentRequest) => Promise<Subscription>;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  hasActiveSubscription: () => boolean;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<RazorpayPlan[]>([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState<ActiveSubscriptionsData | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);

  const getSubscription = useCallback(async (): Promise<Subscription | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<GetSubscriptionResponse>(API_ENDPOINTS.GET_SUBSCRIPTION);
      const sub = response.data.data;
      setSubscription(sub);
      return sub;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setSubscription(null);
        return null;
      }
      const message = err?.response?.data?.message || err.message || 'Failed to get subscription';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTrialStatus = useCallback(async (): Promise<TrialStatus | null> => {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: TrialStatus }>(
        API_ENDPOINTS.GET_TRIAL_STATUS
      );
      const data = response.data.data;
      setTrialStatus(data);
      return data;
    } catch (err: any) {
      // Trial endpoint failure shouldn't block the dashboard — log and return null.
      console.error('Failed to fetch trial status', err);
      setTrialStatus(null);
      return null;
    }
  }, []);

  const fetchActiveSubscriptions = useCallback(async (): Promise<ActiveSubscriptionsData | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<GetActiveSubscriptionsResponse>(API_ENDPOINTS.GET_ACTIVE_SUBSCRIPTIONS);
      const data = response.data.data;
      setActiveSubscriptions(data);
      return data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setActiveSubscriptions(null);
        return null;
      }
      const message = err?.response?.data?.message || err.message || 'Failed to get active subscriptions';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async (): Promise<RazorpayPlan[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<GetPlansResponse>(API_ENDPOINTS.GET_PLANS);
      const fetchedPlans = response.data.data.plans;
      setPlans(fetchedPlans);
      return fetchedPlans;
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Failed to fetch plans';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const validateSubscription = useCallback(
    async (data: ValidateSubscriptionRequest): Promise<ValidateSubscriptionResponse['data']> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.post<ValidateSubscriptionResponse>(API_ENDPOINTS.VALIDATE_SUBSCRIPTION, data);
        return response.data.data;
      } catch (err: any) {
        const message = err?.response?.data?.message || err.message || 'Failed to validate subscription';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const createSubscription = useCallback(
    async (data: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse['data']> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.post<CreateSubscriptionResponse>(API_ENDPOINTS.CREATE_SUBSCRIPTION, data);
        return response.data.data;
      } catch (err: any) {
        const message = err?.response?.data?.message || err.message || 'Failed to create subscription';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const upgradeSubscription = useCallback(
    async (data: UpgradeSubscriptionRequest): Promise<UpgradeSubscriptionResponse['data']> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.post<UpgradeSubscriptionResponse>(API_ENDPOINTS.UPGRADE_SUBSCRIPTION, data);
        return response.data.data;
      } catch (err: any) {
        const message = err?.response?.data?.message || err.message || 'Failed to upgrade subscription';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const verifyPayment = useCallback(
    async (data: VerifyPaymentRequest): Promise<Subscription> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.post<VerifyPaymentResponse>(API_ENDPOINTS.VERIFY_PAYMENT, data);
        const sub = response.data.data.subscription;
        setSubscription(sub);
        return sub;
      } catch (err: any) {
        const message = err?.response?.data?.message || err.message || 'Failed to verify payment';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const cancelSubscription = useCallback(
    async (subscriptionId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        await axiosInstance.delete(`${API_ENDPOINTS.CANCEL_SUBSCRIPTION}/${subscriptionId}/cancel`);
        setSubscription(null);
      } catch (err: any) {
        const message = err?.response?.data?.message || err.message || 'Failed to cancel subscription';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const hasActiveSubscription = useCallback((): boolean => {
    if (!subscription) return false;
    if (subscription.status !== 'active') return false;

    const endDate = new Date(subscription.end_date);
    const now = new Date();
    return endDate > now;
  }, [subscription]);

  return {
    isLoading,
    error,
    subscription,
    plans,
    activeSubscriptions,
    trialStatus,
    getSubscription,
    fetchActiveSubscriptions,
    fetchTrialStatus,
    fetchPlans,
    validateSubscription,
    createSubscription,
    upgradeSubscription,
    verifyPayment,
    cancelSubscription,
    hasActiveSubscription,
  };
};

export default useSubscription;
